/**
 * Reports the signed-in user's activity for Admin → Login Activity sessions:
 * an "open" each time they land on a dashboard they can see, and a "still
 * active" ping at most every 10 minutes while they are actually using the app.
 * Renders nothing and never shows an error.
 *
 * Reports only come from a visible tab, so tabs a browser restores in the
 * background don't invent sessions; a dashboard reached while the tab was
 * hidden is reported once it's shown. A tab switch inside a dashboard is not a
 * new open (only the dashboard is recorded), and neither is a permissions
 * reload; leaving for the launcher and coming back is.
 */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { User } from 'firebase/auth';
import { dashboardForPath } from '../config/dashboards';
import { usePermissions } from '../hooks/usePermissions';
import { postWebActivity } from '../services/activityApi';

/** Lets redirects (Takeovers → Creators) and StrictMode's double effects settle. */
export const OPEN_SETTLE_MS = 500;
/** How often a ping is considered. */
export const PING_CHECK_MS = 60 * 1000;
/** Minimum gap between reports, and the longest idle a ping may report. */
export const PING_INTERVAL_MS = 10 * 60 * 1000;

interface ActivityTrackerProps {
  user: User | null;
  /** Epoch ms of the user's last input (useInactivityTimeout's getLastActivity). */
  getLastActivity: () => number;
}

const ActivityTracker: React.FC<ActivityTrackerProps> = ({ user, getLastActivity }) => {
  const uid = user?.uid ?? null;
  const location = useLocation();
  const { loading, has } = usePermissions();
  const entry = dashboardForPath(location.pathname);
  const allowed = entry !== null && (!entry.permission || (!loading && has(entry.permission)));
  const dashboardId = allowed ? entry.id : null;
  const onDashboard = entry !== null;

  const lastOpenedRef = useRef<string | null>(null);
  const lastReportAtRef = useRef(0);
  const getLastActivityRef = useRef(getLastActivity);
  getLastActivityRef.current = getLastActivity;

  // Another user (or none) starts from scratch.
  useEffect(() => {
    lastOpenedRef.current = null;
    lastReportAtRef.current = 0;
  }, [uid]);

  // Leaving the dashboards makes the next one a new open.
  useEffect(() => {
    if (!onDashboard) lastOpenedRef.current = null;
  }, [onDashboard]);

  useEffect(() => {
    if (!uid || !dashboardId || lastOpenedRef.current === dashboardId) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        if (document.visibilityState !== 'visible' || lastOpenedRef.current === dashboardId) return;
        lastOpenedRef.current = dashboardId;
        lastReportAtRef.current = Date.now();
        void postWebActivity({ kind: 'open', dashboard: dashboardId });
      }, OPEN_SETTLE_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') settle();
    };
    if (document.visibilityState === 'visible') settle();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [uid, dashboardId]);

  useEffect(() => {
    if (!uid) return undefined;
    const check = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      const lastInput = getLastActivityRef.current();
      if (now - lastReportAtRef.current < PING_INTERVAL_MS) return; // reported recently
      if (lastInput <= lastReportAtRef.current) return; // no input since the last report
      if (now - lastInput > PING_INTERVAL_MS) return; // input too long ago (e.g. woke from sleep)
      lastReportAtRef.current = now;
      void postWebActivity({ kind: 'ping', idleSec: Math.round((now - lastInput) / 1000) });
    };
    const interval = setInterval(check, PING_CHECK_MS);
    return () => clearInterval(interval);
  }, [uid]);

  return null;
};

export default ActivityTracker;
